import { useCallback, useState } from 'react';
import { useDropzone } from 'react-dropzone';
import type { SubmitHandler, UseFormReturn } from 'react-hook-form';

import { updateSnackbarNotification } from 'reduxStore/features/notifications/notificationsSlice';
import { useAppDispatch } from 'reduxStore/hooks';

import { useMessages } from '../../../contexts/MessagesContext';
import type { NewTicketMessageFormValuesT } from '../declarations';

type UseMessageFormProps = {
  assignToWarrCloud: boolean;
  requiresAttachment: boolean;
  fromMethods: UseFormReturn<NewTicketMessageFormValuesT>;
};

type UseMessageFormReturn = {
  handleSubmitForm: SubmitHandler<NewTicketMessageFormValuesT>;
  previewImage: string | null;
  handleImagePaste: (event: React.ClipboardEvent<HTMLDivElement>) => void;
  handleRemoveImage: () => void;
  getRootProps: ReturnType<typeof useDropzone>['getRootProps'];
  getInputProps: ReturnType<typeof useDropzone>['getInputProps'];
};

const useMessageForm = ({
  assignToWarrCloud,
  requiresAttachment,
  fromMethods,
}: UseMessageFormProps): UseMessageFormReturn => {
  const dispatch = useAppDispatch();
  const { handleCreateUnsavedMessage } = useMessages();
  const [previewImage, setPreviewImage] = useState<string | null>(null);

  const handleSubmitForm: SubmitHandler<NewTicketMessageFormValuesT> = useCallback(
    (data): void => {
      handleCreateUnsavedMessage(data, assignToWarrCloud);
    },
    [handleCreateUnsavedMessage, assignToWarrCloud]
  );

  const { getRootProps, getInputProps } = useDropzone({
    multiple: false,
    onDropAccepted: (acceptedFiles: File[]): void => {
      const file = acceptedFiles[0];
      if (file.type === 'image/jpeg' || file.type === 'image/png') {
        fromMethods.setValue('file', file);

        const reader = new FileReader();
        reader.onload = (e): void => setPreviewImage(e.target?.result as string);
        reader.readAsDataURL(file);
      } else {
        dispatch(
          updateSnackbarNotification({
            snackbarMessage: 'Only JPEG and PNG files are allowed.',
            snackBarSeverity: 'warning',
          })
        );
      }
    },
    noClick: true,
    disabled: !requiresAttachment,
  });

  const handleImagePaste = (event: React.ClipboardEvent<HTMLDivElement>): void => {
    if (!requiresAttachment) return;

    const pastedData = event.clipboardData.items;
    if (pastedData) {
      const imageItem = pastedData[0];
      if (imageItem.kind === 'file') {
        const file = imageItem.getAsFile();
        if (file?.type === 'image/jpeg' || file?.type === 'image/png') {
          fromMethods.setValue('file', file);
          const reader = new FileReader();
          reader.onload = (e): void => setPreviewImage(e.target?.result as string);
          reader.readAsDataURL(file);
        }
      }
    }
  };

  const handleRemoveImage = (): void => {
    setPreviewImage(null);
    fromMethods.setValue('file', null);
  };

  return {
    handleSubmitForm,
    previewImage,
    handleImagePaste,
    handleRemoveImage,
    getRootProps,
    getInputProps,
  };
};

export default useMessageForm;
